import { useEffect } from 'react'

import { Nullable } from '@types'

import searchApi from '@modules/search/api'
import { CompanyAssetHistoryProfile, CompanyHistoryProfile, DelegateHistoryProfile, ProfileType } from '@modules/search/types'
import { mapApiProfileTypeToUI } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'

const API_BY_TYPE = {
  [ProfileType.biotech]: searchApi.getAssetProfile,
  [ProfileType.medtech]: searchApi.getAssetProfile,
  [ProfileType.technologies]: searchApi.getAssetProfile,
  [ProfileType.services]: searchApi.getAssetProfile,
  [ProfileType.company]: searchApi.getCompanyProfile,
  [ProfileType.delegate]: searchApi.getDelegateProfile,
}

interface TypeMaps {
  [ProfileType.biotech]: CompanyAssetHistoryProfile
  [ProfileType.medtech]: CompanyAssetHistoryProfile
  [ProfileType.technologies]: CompanyAssetHistoryProfile
  [ProfileType.services]: CompanyAssetHistoryProfile
  [ProfileType.company]: CompanyHistoryProfile
  [ProfileType.delegate]: DelegateHistoryProfile
}

const useFetchProfile = <T extends ProfileType>({
  id,
  type,
  disabled,
  onError,
}: {
  id?: Nullable<string | number>
  type: T
  disabled?: boolean
  onError?: () => void
}) => {
  const api = API_BY_TYPE[type]

  const { data, ...rest } = useQuery<TypeMaps[keyof TypeMaps]>({
    queryKey: [api.key, String(id), type],
    queryFn: () => api.fn({ id: id as string }),
    enabled: disabled ? false : !!id,
    refetchOnMount: false,
    retry: false,
  })

  useEffect(() => {
    const { status } = rest
    if (status === 'error') onError?.()
  }, [rest])

  return {
    data: data && (mapApiProfileTypeToUI([data])[0] as TypeMaps[T]),
    ...rest,
  }
}

export default useFetchProfile
